import { api } from './client';

// settings
export const settingsApi = {
  list: async () => (await api<{ data: any[] }>('/api/v2/settings')).data,
  set: async (key: string, value: string) => api(`/api/v2/settings/${encodeURIComponent(key)}`, { method: 'PUT', body: { setting_value: value } }),
};

// app_integration_settings
export const integrationsApi = {
  list: async () => (await api<{ data: any[] }>('/api/v2/integrations')).data,
  get: async (provider: string) => (await api<{ data: any }>(`/api/v2/integrations/${encodeURIComponent(provider)}`)).data,
  set: async (provider: string, settings: any) => api(`/api/v2/integrations/${encodeURIComponent(provider)}`, { method: 'PUT', body: { settings } }),
};

// automation_rules
export const automationApi = {
  list: async () => (await api<{ data: any[] }>('/api/v2/automation-rules')).data,
  create: async (b: any) => api<{ id: string }>('/api/v2/automation-rules', { method: 'POST', body: b }),
  update: async (id: string, b: any) => api(`/api/v2/automation-rules/${id}`, { method: 'PUT', body: b }),
  remove: async (id: string) => api(`/api/v2/automation-rules/${id}`, { method: 'DELETE' }),
};

// inventory_logs
export interface InventoryLog {
  id: number;
  product_id: number;
  product_name?: string;
  performed_by: number | null;
  performed_by_name?: string | null;
  action_type: string;
  quantity: number;
  quantity_before: number;
  quantity_after: number;
  quantity_change: number;
  reason?: string | null;
  created_at: string;
}

export const inventoryApi = {
  list: async (q: { product_id?: number; limit?: number } = {}) => {
    const r = await api<{ data: InventoryLog[] }>('/api/v2/inventory-logs', { query: q as any });
    return r.data;
  },
  adjust: async (productId: number, body: { quantity_change: number; action_type: string; reason?: string }) =>
    api(`/api/v2/products/${productId}/stock-adjust`, { method: 'POST', body }),
  // Spec v2 Rule 2: adjustments cannot be deleted; only reversed. Backend
  // creates a counter log + reverts stock_quantity when applicable.
  reverse: async (logId: number, reason: string) =>
    api(`/api/v2/inventory-logs/${logId}/reverse`, { method: 'POST', body: { reason } }),
};

// invoices
export const invoicesApi = {
  list: async () => (await api<{ data: any[] }>('/api/v2/invoices')).data,
  get: async (id: string) => (await api<{ data: any }>(`/api/v2/invoices/${id}`)).data,
  create: async (b: any) => api<{ id: string }>('/api/v2/invoices', { method: 'POST', body: b }),
};

// delivery_status
export const deliveryApi = {
  list: async (orderId?: number) => (await api<{ data: any[] }>('/api/v2/delivery-status', { query: { order_id: orderId } })).data,
  add: async (b: { order_id: number; status: string; date?: string }) => api('/api/v2/delivery-status', { method: 'POST', body: b }),
};

// courier_bookings
export const courierBookingsApi = {
  list: async (orderId?: number) => (await api<{ data: any[] }>('/api/v2/courier-bookings', { query: { order_id: orderId } })).data,
  get: async (id: number) => (await api<{ data: any }>(`/api/v2/courier-bookings/${id}`)).data,
  create: async (b: any) => api<{ id: number }>('/api/v2/courier-bookings', { method: 'POST', body: b }),
  update: async (id: number, b: any) => api(`/api/v2/courier-bookings/${id}`, { method: 'PUT', body: b }),
};

// waha_followups
export const wahaFollowupsApi = {
  list: async () => (await api<{ data: any[] }>('/api/v2/waha-followups')).data,
  create: async (b: any) => api<{ id: string }>('/api/v2/waha-followups', { method: 'POST', body: b }),
  update: async (id: string, b: any) => api(`/api/v2/waha-followups/${id}`, { method: 'PUT', body: b }),
  remove: async (id: string) => api(`/api/v2/waha-followups/${id}`, { method: 'DELETE' }),
};

// user_preferences
export const userPreferencesApi = {
  get: async (userId: number) => (await api<{ data: any }>(`/api/v2/user-preferences/${userId}`)).data,
  set: async (userId: number, preferences: any) => api(`/api/v2/user-preferences/${userId}`, { method: 'PUT', body: { preferences } }),
};

// sales_report
export const salesReportApi = {
  list: async () => (await api<{ data: any[] }>('/api/v2/sales-report')).data,
};
